import BaseListPresenter from "../../base/BaseListPresenter";
import Papa from "papaparse";
import schemaDashboard from "./schemaDashboard.json";
import { getCurrentUserUseCase } from "../../usecases/user";
import {
  format,
  startOfDay,
  addHours,
  addMinutes,
  addDays,
  parseISO,
} from "date-fns";

class ReportDashPresenter extends BaseListPresenter {
  init() {
    this.limit = 50000;
    this.where = {};
    this.search = {};
    this.filter = {};
    this.filterDate = {};
    this.filterStore = {};
    this.include = ["all"];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { createdAt: -1 };
    this.progress = true;
    this.reset();
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: {
        ...this.where,
        ...this.search,
        ...this.filter,
        ...this.filterDate,
      },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }
  async findObjects() {
    // 1) Load user + store (kailangan bago gumawa ng whereClause)
    try {
      const user = await getCurrentUserUseCase().execute();
      const storeQuery = { include: ["all"], where: { email: user.email } };
      this.store = await this.findObjectUseCase.execute("users", storeQuery);

      const store = (this.store?.[0]?.storeAnalytic ?? []).sort((a, b) =>
        a.name.toUpperCase().localeCompare(b.name.toUpperCase())
      );
      this.view.setStore(store);
    } catch (error) {
      console.error("Error fetching user or store data:", error);
      return;
    }

    // 2) Build base filters (AllStore -> $in ng lahat ng analytic names)
    const storeCodes =
      this.store?.[0]?.storeAnalytic?.map((a) => a?.name).filter(Boolean) ?? [];

    const whereClause = {
      ...this.filterDate,
      store_code:
        this.filterStore === "AllStore"
          ? { $in: storeCodes }
          : this.filterStore,
    };

    // fixed: true batching size
    const pageSize = this.limit || 50000;

    // 3) Count muna gamit ang full filter
    let totalCount = 0;
    try {
      this.showProgress();
      this.findObjectUseCase.abort(); // cancel any in-flight

      const { count } = await this.countObjectUseCase.execute(
        this.view.getCollectionName(),
        { where: whereClause }
      );

      totalCount = count ?? 0;
      this.count = totalCount;
      this.view.setCount(totalCount);

      // Reset UI state
      this.objects = [];
      this.view.setObjects([]);
      this.view.setTotal(0);
      this.view.setNotFound(false);

      // Not-found delay (optional)
      let timeoutId = null;
      if (totalCount === 0) {
        timeoutId = setTimeout(() => {
          if (this.objects.length === 0) {
            console.log("No data after delay. Setting NotFound = true");
            this.view.setNotFound(true);
          }
        }, 30000);
        // Walang data — wala nang ipe-fetch na pages
        if (timeoutId) clearTimeout(timeoutId);
        return;
      }

      // 4) Batch pagination loop
      let fetched = 0;
      let page = 0;

      // Shared buffer + periodic flush (para smooth ang UI)
      let buffer = [];
      const flushBuffer = () => {
        if (buffer.length === 0) return;
        this.objects = this.objects.concat(buffer);
        this.view.setObjects(this.objects);
        this.view.setTotal(this.objects.length);
        buffer = [];
      };
      const flushInterval = setInterval(flushBuffer, 1000);

      try {
        while (fetched < totalCount) {
          const skip = page * pageSize;

          const query = {
            skip,
            limit: pageSize, // <<— important: 20 per batch
            where: whereClause,
            include: ["all"],
            sort: this.sort,
            keys: [
              "store_code",
              "tran_date",
              "total",
              "total_taxfree",
              "total_tax",
              "discount_amount",
              "createdAt",
              "cust_name",
              "tran_type",
              "paymenttype",
              "cust_count",
              "total_pretax",
              "status",
            ],
          };

          // Per-page local buffer via onData
          let pageRowCount = 0;
          const onData = (objects) => {
            if (objects?.length) {
              buffer.push(...objects);
              pageRowCount += objects.length;
            }
          };

          // Execute one page (may stream sa loob, pero hanggang `limit` lang)
          await this.findObjectUseCase.execute(
            this.view.getCollectionName(),
            query,
            { timeout: 120000, onData }
          );

          // Push whatever came in this page
          flushBuffer();

          // Walang dumating sa page na ito → tapos na
          if (pageRowCount === 0) break;

          fetched += pageRowCount;
          page += 1;

          // Yield para hindi ma-block UI thread
          await Promise.resolve();
        }
      } finally {
        clearInterval(flushInterval);
        flushBuffer();
      }

      // Safeguard
      if (this.objects.length === 0) {
        this.view.setNotFound(true);
      }
    } catch (error) {
      console.error("Error fetching objects:", error);
      this.view.showError(error);
    } finally {
      this.hideProgress();
      this.progress = false;
    }
  }
  // async findObjects() {
  //   try {
  //     const user = await getCurrentUserUseCase().execute();
  //     const storeQuery = {
  //       include: ["all"],
  //       where: { email: user.email },
  //     };
  //     this.store = await this.findObjectUseCase.execute("users", storeQuery);
  //     const store = this.store[0].storeAnalytic.sort((a, b) => {
  //       const nameA = a.name.toUpperCase();
  //       const nameB = b.name.toUpperCase();
  //       if (nameA < nameB) {
  //         return -1;
  //       }
  //       if (nameA > nameB) {
  //         return 1;
  //       }
  //       return 0; // names must be equal
  //     });
  //     this.view.setStore(store);
  //   } catch (error) {
  //     console.error("Error fetching user or store data:", error);
  //     return;
  //   }

  //   const skip = (this.current - 1) * this.limit;
  //   const storeCodes = this.store?.flatMap((storeItem) =>
  //     storeItem.storeAnalytic?.map((analytic) => analytic?.name)
  //   );

  //   const query = {
  //     // limit: 800000,
  //     skip: skip,
  //     where: {},
  //     where: {
  //       store_code:
  //         this.filterStore === "AllStore"
  //           ? { $in: storeCodes }
  //           : this.filterStore,
  //       ...this.filterDate,
  //     },
  //     include: ["all"],
  //     sort: this.sort,
  //     keys: [
  //       "store_code",
  //       "tran_date",
  //       "total",
  //       "total_taxfree",
  //       "total_tax",
  //       "discount_amount",
  //       "createdAt",
  //       "cust_name",
  //       "tran_type",
  //       "paymenttype",
  //       "cust_count",
  //       "status",
  //     ],
  //   };

  //   try {
  //     this.showProgress();
  //     this.findObjectUseCase.abort(); // Abort any previous use case execution
  //     // const objects = await this.findObjectUseCase.execute(
  //     //   this.view.getCollectionName(),
  //     //   query
  //     // );

  //     if (Object.keys(this.filterDate).length === 0) {
  //       console.log("sample");
  //     } else {
  //       // this.objects = this.objects.concat(objects);
  //       // this.view.setTotal(this.objects.length);
  //       // this.view.setObjects(this.objects);
  //       const { count } = await this.countObjectUseCase.execute(
  //         this.view.getCollectionName(),
  //         {
  //           where: query.where,
  //         }
  //       );
  //       this.count = count;
  //       console.log("this.count", this.count);
  //       this.view.setCount(this.count);

  //       // Batching logic
  //       let buffer = [];
  //       let flushInterval = null;

  //       const flushBuffer = () => {
  //         if (buffer.length > 0) {
  //           this.view.setState((prevState) => ({
  //             objects: [...prevState.objects, ...buffer],
  //           }));
  //           this.objects = this.objects.concat(buffer);
  //           buffer = [];
  //           this.view.setTotal(this.objects.length);
  //           this.view.setObjects(this.objects);
  //         }
  //       };

  //       const onData = (objects) => {
  //         buffer.push(...objects);
  //       };

  //       flushInterval = setInterval(flushBuffer, 2000);

  //       const objects = await this.findObjectUseCase.execute(
  //         this.view.getCollectionName(),
  //         query,
  //         {
  //           timeout: 100000,
  //           onData,
  //         }
  //       );

  //       // Final flush and cleanup
  //       clearInterval(flushInterval);
  //       flushBuffer();

  //       if (!this.count) {
  //         const not = true;
  //         // alert("wrok");
  //         this.view.setObjects([]);
  //         this.view.setNotFound(not);
  //       }
  //     }
  //   } catch (error) {
  //     console.error("Error fetching objects:", error);
  //     this.view.showError(error);
  //   } finally {
  //     this.hideProgress();
  //     this.progress = false;
  //   }
  // }

  // async findObjects() {
  //   try {
  //     // 1) Load user + store
  //     const user = await getCurrentUserUseCase().execute();
  //     const storeQuery = { include: ["all"], where: { email: user.email } };
  //     this.store = await this.findObjectUseCase.execute("users", storeQuery);

  //     const store = (this.store?.[0]?.storeAnalytic ?? []).sort((a, b) =>
  //       a.name.toUpperCase().localeCompare(b.name.toUpperCase())
  //     );
  //     this.view.setStore(store);
  //   } catch (error) {
  //     console.error("Error fetching user or store data:", error);
  //     return;
  //   }

  //   // 2) Build filters safely
  //   const storeCodes =
  //     this.store?.[0]?.storeAnalytic?.map((a) => a?.name).filter(Boolean) ?? [];

  //   if (!this.filterDate || Object.keys(this.filterDate).length === 0) {
  //     console.log("No filterDate; aborting to avoid massive scan.");
  //     return;
  //   }

  //   const where = {
  //     store_code:
  //       this.filterStore === "AllStore"
  //         ? { $in: storeCodes }
  //         : this.filterStore,
  //     ...this.filterDate,
  //   };

  //   // 3) Prep + count
  //   this.showProgress();
  //   try {
  //     this.findObjectUseCase.abort(); // cancel any in-flight

  //     const { count } = await this.countObjectUseCase.execute(
  //       this.view.getCollectionName(),
  //       { where }
  //     );

  //     this.count = count ?? 0;
  //     this.view.setCount(this.count);

  //     // Reset UI
  //     this.objects = [];
  //     this.view.setObjects([]);
  //     this.view.setTotal(0);
  //     this.view.setNotFound(false);

  //     // if (this.count === 0) {
  //     //   // Walang laman → mark not found
  //     //   this.view.setNotFound(true);
  //     //   return;
  //     // }

  //     // 4) Batch pagination loop (20 per batch by default)
  //     const pageSize = this.limit || 50000;
  //     let page = Math.max(0, (this.current || 1) - 1);
  //     let fetched = 0;

  //     // Shared buffer + periodic flush (smooth UI)
  //     let buffer = [];
  //     const flushBuffer = () => {
  //       if (!buffer.length) return;
  //       // internal
  //       this.objects = this.objects.concat(buffer);
  //       // UI
  //       this.view.setObjects(this.objects);
  //       this.view.setTotal(this.objects.length);
  //       buffer = [];
  //     };
  //     const flushInterval = setInterval(flushBuffer, 2000);

  //     try {
  //       while (fetched < this.count) {
  //         const skip = page * pageSize;

  //         const query = {
  //           limit: pageSize,
  //           skip,
  //           where,
  //           include: ["all"],
  //           sort: this.sort,
  //           keys: [
  //             "store_code",
  //             "tran_date",
  //             "total",
  //             "total_taxfree",
  //             "total_tax",
  //             "discount_amount",
  //             "createdAt",
  //             "cust_name",
  //             "tran_type",
  //             "paymenttype",
  //             "cust_count",
  //             "status",
  //           ],
  //         };

  //         // Per-page accumulator via onData
  //         let pageRowCount = 0;
  //         const onData = (rows) => {
  //           if (rows?.length) {
  //             buffer.push(...rows);
  //             pageRowCount += rows.length;
  //           }
  //         };

  //         // Fetch one page (server should honor limit)
  //         await this.findObjectUseCase.execute(
  //           this.view.getCollectionName(),
  //           query,
  //           { timeout: 100000, onData }
  //         );

  //         // Push streamed rows for this page
  //         flushBuffer();

  //         if (pageRowCount === 0) break; // no more rows
  //         fetched += pageRowCount;
  //         page += 1;

  //         // Yield to UI thread
  //         await Promise.resolve();
  //       }
  //     } finally {
  //       clearInterval(flushInterval);
  //       flushBuffer();
  //     }

  //     // Safeguard
  //     if (this.objects.length === 0) {
  //       this.view.setNotFound(true);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching objects:", error);
  //     this.view.showError(error);
  //   } finally {
  //     this.hideProgress();
  //     this.progress = false;
  //   }
  // }

  onChangeDate(where) {
    const { tran_date } = where;

    if (tran_date && tran_date.$gte && tran_date.$lte) {
      const startDate = addHours(startOfDay(parseISO(tran_date.$gte)), 5);
      const endDate = addMinutes(
        addHours(addDays(startOfDay(parseISO(tran_date.$lte)), 0), 4),
        59
      );

      const formattedStartDate = format(startDate, "yyyy-MM-dd HH:mm:ss.SSS");
      const formattedEndDate = format(endDate, "yyyy-MM-dd HH:mm:ss.SSS");

      this.filterDate = {
        tran_date: {
          $gte: formattedStartDate,
          $lte: formattedEndDate,
        },
      };
    }

    this.getObjects();
  }

  onChangeStore(where) {
    this.filterStore = where;
    this.getObjects();
  }

  calculateSums(objects) {
    const sumReducer = (acc, object, key) => acc + Number(object[key] || 0);
    const sum_total = objects?.reduce(
      (acc, obj) => sumReducer(acc, obj, "total"),
      0
    );
    const sum_discount = objects?.reduce(
      (acc, obj) => sumReducer(acc, obj, "discount_amount"),
      0
    );
    const sum_taxes = objects?.reduce(
      (acc, obj) => sumReducer(acc, obj, "total_tax"),
      0
    );

    return {
      sum_total,
      sum_discount,
      sum_taxes,
      grossSum: sum_total + sum_discount,
      grossSales: sum_total + sum_discount - sum_taxes,
    };
  }

  calculateDeliveryInfo() {
    const transactions = this.objects?.filter((obj) => obj.status === "C");
    const totalTrans = transactions
      ?.map((t) => t)
      ?.filter((t) => t.tran_type === "U");
    const totalSale = totalTrans
      ?.map((t) => Number(t.total))
      ?.reduce((acc, curr) => acc + curr, 0);

    const totalDiscounts = 0;
    const netSales = totalSale - totalDiscounts;

    return {
      value: netSales,
    };
  }

  calculateDineInfo() {
    const transactions = this.objects?.filter((obj) => obj.status === "C");
    const totalTrans = transactions
      ?.map((t) => t)
      ?.filter((t) => t.tran_type === "D");
    const totalSale = totalTrans
      ?.map((t) => Number(t.total))
      ?.reduce((acc, curr) => acc + curr, 0);

    const totalDiscounts = 0;
    const netSales = totalSale - totalDiscounts;

    return {
      value: netSales,
    };
  }

  calculateTakeOutInfo() {
    const transactions = this.objects?.filter((obj) => obj.status === "C");
    const totalTrans = transactions
      ?.map((t) => t)
      ?.filter((t) => t.tran_type === "T");
    const totalSale = totalTrans
      ?.map((t) => Number(t.total))
      ?.reduce((acc, curr) => acc + curr, 0);

    const totalDiscounts = 0;
    const netSales = totalSale - totalDiscounts;

    return {
      value: netSales,
    };
  }

  calculateGrabFoodInfo() {
    const transactions = this.objects?.filter((obj) => obj.status === "C");
    const totalTrans = transactions
      ?.map((t) => t)
      ?.filter((t) => t.tran_type === "G");
    const totalSale = totalTrans
      ?.map((t) => Number(t.total))
      ?.reduce((acc, curr) => acc + curr, 0);

    const totalDiscounts = 0;
    const netSales = totalSale - totalDiscounts;

    return {
      value: netSales,
    };
  }

  calculatePickUpInfo() {
    const transactions = this.objects?.filter((obj) => obj.status === "C");
    const totalTrans = transactions
      ?.map((t) => t)
      ?.filter((t) => t.tran_type === "P");
    const totalSale = totalTrans
      ?.map(
        (t) => Number(t.total) + Number(t.discount_amount) - Number(t.total_tax)
      )
      ?.reduce((acc, curr) => acc + curr, 0);

    const totalDiscounts = totalTrans
      ?.map((t) => Number(t.discount_amount))
      .reduce((acc, curr) => acc + curr, 0);

    const netSales = totalSale - totalDiscounts;

    return {
      value: netSales,
    };
  }

  calculateDelTimeInfo() {
    const transactions = this.objects?.filter((obj) => obj.status === "C");
    const totalTrans = transactions
      ?.map((t) => t)
      ?.filter((t) => t.tran_type === "B");
    const totalSale = totalTrans
      ?.map(
        (t) => Number(t.total) + Number(t.discount_amount) - Number(t.total_tax)
      )
      ?.reduce((acc, curr) => acc + curr, 0);

    const totalDiscounts = totalTrans
      ?.map((t) => Number(t.discount_amount))
      .reduce((acc, curr) => acc + curr, 0);

    const netSales = totalSale - totalDiscounts;

    return {
      value: netSales,
    };
  }

  calculateDriveInfo() {
    const transactions = this.objects?.filter((obj) => obj.status === "C");
    const totalTrans = transactions
      ?.map((t) => t)
      ?.filter((t) => t.tran_type === "K");
    const totalSale = totalTrans
      ?.map((t) => Number(t.total))
      ?.reduce((acc, curr) => acc + curr, 0);

    const totalDiscounts = 0;
    const netSales = totalSale - totalDiscounts;

    return {
      value: netSales,
    };
  }

  calculatePandaInfo() {
    const transactions = this.objects?.filter((obj) => obj.status === "C");
    const totalTrans = transactions
      ?.map((t) => t)
      ?.filter((t) => t.tran_type === "F");
    const totalSale = totalTrans
      ?.map((t) => Number(t.total))
      ?.reduce((acc, curr) => acc + curr, 0);

    const totalDiscounts = 0;
    const netSales = totalSale - totalDiscounts;

    return {
      value: netSales,
    };
  }

  calculatePercentage(current, previous) {
    if (previous > 0 && current !== undefined) {
      return parseFloat(((current - previous) / previous) * 100).toFixed(2);
    }
    return 0;
  }

  countCustomers() {
    return this.objects
      ?.filter((cus) => cus.cust_name !== "-")
      .map((cust) => cust.cust_name).length;
  }

  onClickExport(filteredObj) {
    const gross_sales = filteredObj
      .map(
        (object) =>
          Number(object.total) +
          Number(object.discount_amount) -
          Number(object.total_tax)
      )
      .reduce((acc, curr) => acc + curr, 0);
    const totalDiscountAmount = filteredObj.reduce(
      (acc, object) => acc + Number(object.discount_amount),
      0
    );
    const totalPretax = filteredObj.reduce(
      (acc, object) => acc + Number(object.total_pretax),
      0
    );
    const totalTax = filteredObj.reduce(
      (acc, object) => acc + Number(object.total_tax),
      0
    );
    const totalTaxFree = filteredObj.reduce(
      (acc, object) => acc + Number(object.total_taxfree),
      0
    );

    const staticDataRow = {
      gross_sales: gross_sales,
      net_sales: gross_sales - totalDiscountAmount,
      pretax: totalPretax,
      tax: totalTax,
      tax_free: totalTaxFree,
      tax_free_amount: "",
      discount_total: totalDiscountAmount,
    };

    const data = schemaDashboard.map((object, index) => {
      const { value, percentage } = this.getValueForDashboardObject(object);
      const additionalData = index === 0 ? staticDataRow : {};
      return {
        trans_type: object.label,
        TotalSales: value,
        start_date: this.state.startDate,
        end_date: this.state.endDate,
        "": "",

        ...additionalData,
      };
    });

    const csv = Papa.unparse(data);

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "dashboard_data.csv");
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

export default ReportDashPresenter;
